import {
  Badge,
  Box,
  Grid,
  HStack,
  Icon,
  Input,
  Spinner,
  Text,
  VStack
} from "@chakra-ui/react";
import React from "react";
import {
  FiChevronDown,
  FiFolder,
  FiGitBranch,
  FiGithub,
  FiInfo,
  FiSearch
} from "react-icons/fi";

interface GithubIntegrationProps {
  githubRepo: string;
  githubSubfolder: string;
  githubTag: string;
  repoDropdown: {
    isOpen: boolean;
    searchQuery: string;
  };
  subfolderDropdown: {
    isOpen: boolean;
    searchQuery: string;
  };
  tagDropdown: {
    isOpen: boolean;
    searchQuery: string;
  };
  validationErrors: Record<string, string>;
  isValidatingRepo: boolean;
  isLoadingSubfolders: boolean;
  isLoadingTags: boolean;
  availableRepos: string[];
  subfolders: string[];
  tags: string[];
  onRepoChange: (repo: string) => void;
  onSubfolderChange: (subfolder: string) => void;
  onTagChange: (tag: string) => void;
  onRepoDropdownToggle: (isOpen: boolean) => void;
  onRepoSearchChange: (query: string) => void;
  onSubfolderDropdownToggle: (isOpen: boolean) => void;
  onSubfolderSearchChange: (query: string) => void;
  onTagDropdownToggle: (isOpen: boolean) => void;
  onTagSearchChange: (query: string) => void;
}

export const GithubIntegration: React.FC<GithubIntegrationProps> = ({
  githubRepo,
  githubSubfolder,
  githubTag,
  repoDropdown,
  subfolderDropdown,
  tagDropdown,
  validationErrors,
  isValidatingRepo,
  isLoadingSubfolders,
  isLoadingTags,
  availableRepos,
  subfolders,
  tags,
  onRepoChange,
  onSubfolderChange,
  onTagChange,
  onRepoDropdownToggle,
  onRepoSearchChange,
  onSubfolderDropdownToggle,
  onSubfolderSearchChange,
  onTagDropdownToggle,
  onTagSearchChange
}) => {
  // Filter repos based on search
  const filteredRepos = availableRepos.filter(repo =>
    repo.toLowerCase().includes(repoDropdown.searchQuery.toLowerCase())
  );

  // Filter subfolders based on search
  const filteredSubfolders = subfolders.filter(folder =>
    folder.toLowerCase().includes(subfolderDropdown.searchQuery.toLowerCase())
  );

  // Filter tags based on search
  const filteredTags = tags.filter(tag =>
    tag.toLowerCase().includes(tagDropdown.searchQuery.toLowerCase())
  );

  return (
    <Box
      bg="white"
      rounded="xl"
      shadow="sm"
      border="1px"
      borderColor="gray.200"
      overflow="visible"
      position="relative"
    >
      <Box
        px={6}
        py={4}
        bg="gradient.to-r"
        bgGradient="linear(to-r, gray.50, purple.50)"
        borderBottom="1px"
        borderColor="gray.200"
      >
        <HStack gap={2}>
          <Icon as={FiGithub} boxSize={5} color="purple.600" />
          <Text fontWeight="semibold" fontSize="lg" color="gray.800">
            GitHub Repository
          </Text>
        </HStack>
      </Box>

      <Box p={6} position="relative" overflow="visible">
        <VStack gap={4} align="stretch" overflow="visible">
          {/* Info Box */}
          <Box
            bg="blue.50"
            border="1px"
            borderColor="blue.200"
            rounded="lg"
            p={3}
          >
            <HStack align="start" gap={2}>
              <Icon as={FiInfo} color="blue.600" mt={0.5} flexShrink={0} />
              <Text fontSize="xs" color="blue.700">
                Select a GitHub repository and optionally a subfolder to serve content from.
                The content will be automatically deployed when you update the repository.
              </Text>
            </HStack>
          </Box>

          {/* GitHub Repository Dropdown */}
          <Box position="relative" zIndex={20}>
            <Text fontSize="sm" fontWeight="medium" mb={2}>
              Repository <Text as="span" color="red.500">*</Text>
            </Text>
            <Box position="relative">
              <Box
                as="button"
                w="100%"
                p={3}
                bg="white"
                border="1px solid"
                borderColor={
                  validationErrors.githubRepo ? "red.500" :
                  repoDropdown.isOpen ? "blue.500" : "gray.300"
                }
                borderRadius="md"
                textAlign="left"
                fontSize="md"
                onClick={() => {
                  onRepoDropdownToggle(!repoDropdown.isOpen);
                  onRepoSearchChange("");
                }}
                _hover={{ borderColor: validationErrors.githubRepo ? "red.600" : "gray.400" }}
                _focus={{
                  borderColor: validationErrors.githubRepo ? "red.500" : "blue.500",
                  boxShadow: validationErrors.githubRepo 
                    ? "0 0 0 1px var(--chakra-colors-red-500)"
                    : "0 0 0 1px var(--chakra-colors-blue-500)",
                  outline: "none"
                }}
              >
                <HStack justify="space-between">
                  <HStack>
                    <Icon as={FiGithub} color="gray.500" />
                    <Text
                      color={githubRepo ? "black" : "gray.500"}
                      fontSize="md"
                    >
                      {githubRepo || "Select repository..."}
                    </Text>
                    {isValidatingRepo && <Spinner size="sm" />}
                  </HStack>
                  <Icon
                    as={FiChevronDown}
                    transform={repoDropdown.isOpen ? "rotate(180deg)" : "rotate(0deg)"}
                    transition="transform 0.2s"
                  />
                </HStack>
              </Box>

              {repoDropdown.isOpen && (
                <Box
                  position="absolute"
                  top="100%"
                  left={0}
                  right={0}
                  mt={1}
                  bg="white"
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="md"
                  shadow="lg"
                  zIndex={1000}
                  maxH="400px"
                  overflow="hidden"
                >
                  <Box p={3} borderBottom="1px solid" borderColor="gray.200">
                    <HStack>
                      <Icon as={FiSearch} color="gray.400" />
                      <Input
                        placeholder="Search repositories..."
                        value={repoDropdown.searchQuery}
                        onChange={(e) => onRepoSearchChange(e.target.value)}
                        size="sm"
                        border="none"
                        _focus={{
                          outline: "none",
                          boxShadow: "none"
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </HStack>
                  </Box>
                  <Box maxH="300px" overflowY="auto">
                    {filteredRepos.map((repo) => (
                      <Box
                        key={repo}
                        px={4}
                        py={3}
                        cursor="pointer"
                        _hover={{ bg: "gray.50" }}
                        onClick={() => {
                          onRepoChange(repo);
                          onSubfolderChange("");
                          onTagChange("");
                          onRepoDropdownToggle(false);
                          onRepoSearchChange("");
                        }}
                      >
                        <HStack>
                          <Icon as={FiGithub} color="gray.400" boxSize={3} />
                          <Text fontSize="md">{repo}</Text>
                        </HStack>
                      </Box>
                    ))}
                    {filteredRepos.length === 0 && (
                      <Box px={4} py={3}>
                        <Text color="gray.500" fontSize="sm">
                          No repositories found
                        </Text>
                      </Box>
                    )}
                  </Box>
                </Box>
              )}
            </Box>
            {validationErrors.githubRepo && (
              <Text color="red.500" fontSize="sm" mt={1}>
                {validationErrors.githubRepo}
              </Text>
            )}
          </Box>

          <Grid templateColumns="repeat(2, 1fr)" gap={4} position="relative" zIndex={10}>
            {/* GitHub Subfolder Dropdown */}
            <Box position="relative" zIndex={15}>
              <Text fontSize="sm" fontWeight="medium" mb={2}>
                Subfolder <Text as="span" color="gray.500" fontSize="xs">(Optional)</Text>
              </Text>
              <Box position="relative">
                <Box
                  as="button"
                  w="100%"
                  p={3}
                  bg={!githubRepo ? "gray.50" : "white"}
                  border="1px solid"
                  borderColor={subfolderDropdown.isOpen ? "blue.500" : "gray.300"}
                  borderRadius="md"
                  textAlign="left"
                  fontSize="md"
                  _disabled={{ opacity: 0.6, cursor: "not-allowed" }}
                  cursor={!githubRepo ? "not-allowed" : "pointer"}
                  pointerEvents={!githubRepo ? "none" : "auto"}
                  opacity={!githubRepo ? 0.6 : 1}
                  onClick={() => {
                    if (githubRepo) {
                      onSubfolderDropdownToggle(!subfolderDropdown.isOpen);
                      onSubfolderSearchChange("");
                    }
                  }}
                  _hover={{ borderColor: githubRepo ? "gray.400" : "gray.300" }}
                  _focus={{
                    borderColor: "blue.500",
                    boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)",
                    outline: "none"
                  }}
                >
                  <HStack justify="space-between">
                    <HStack>
                      <Icon as={FiFolder} color="gray.500" boxSize={4} />
                      <Text
                        color={githubSubfolder ? "black" : "gray.500"}
                        fontSize="md"
                      >
                        {githubSubfolder || "Root"}
                      </Text>
                      {isLoadingSubfolders && <Spinner size="sm" />}
                    </HStack>
                    <Icon
                      as={FiChevronDown}
                      transform={subfolderDropdown.isOpen ? "rotate(180deg)" : "rotate(0deg)"}
                      transition="transform 0.2s"
                    />
                  </HStack>
                </Box>

                {subfolderDropdown.isOpen && (
                  <Box
                    position="absolute"
                    top="100%"
                    left={0}
                    right={0}
                    mt={1}
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="md"
                    shadow="lg"
                    zIndex={10}
                    maxH="400px"
                    overflow="hidden"
                  >
                    <Box p={3} borderBottom="1px solid" borderColor="gray.200">
                      <HStack>
                        <Icon as={FiSearch} color="gray.400" />
                        <Input
                          placeholder="Search folders..."
                          value={subfolderDropdown.searchQuery}
                          onChange={(e) => onSubfolderSearchChange(e.target.value)}
                          size="sm"
                          border="none"
                          _focus={{
                            outline: "none",
                            boxShadow: "none"
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </HStack>
                    </Box>
                    <Box maxH="300px" overflowY="auto">
                      <Box
                        px={4}
                        py={3}
                        cursor="pointer"
                        bg={!githubSubfolder ? "blue.50" : "white"}
                        _hover={{ bg: "gray.50" }}
                        onClick={() => {
                          onSubfolderChange("");
                          onSubfolderDropdownToggle(false);
                          onSubfolderSearchChange("");
                        }}
                      >
                        <HStack>
                          <Icon as={FiFolder} color="gray.400" boxSize={3} />
                          <Text fontSize="md" fontWeight={!githubSubfolder ? "medium" : "normal"}>
                            / (root)
                          </Text>
                        </HStack>
                      </Box>
                      {filteredSubfolders.map((folder) => (
                        <Box
                          key={folder}
                          px={4}
                          py={3}
                          cursor="pointer"
                          bg={githubSubfolder === folder ? "blue.50" : "white"}
                          _hover={{ bg: "gray.50" }}
                          onClick={() => {
                            onSubfolderChange(folder);
                            onSubfolderDropdownToggle(false);
                            onSubfolderSearchChange("");
                          }}
                        >
                          <HStack>
                            <Icon as={FiFolder} color="gray.400" boxSize={3} />
                            <Text fontSize="md" fontWeight={githubSubfolder === folder ? "medium" : "normal"}>
                              /{folder}
                            </Text>
                          </HStack>
                        </Box>
                      ))}
                      {filteredSubfolders.length === 0 && (
                        <Box px={4} py={3}>
                          <Text color="gray.500" fontSize="sm">
                            No subfolders found
                          </Text>
                        </Box>
                      )}
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>

            {/* GitHub Tag Dropdown */}
            <Box position="relative" zIndex={15}>
              <Text fontSize="sm" fontWeight="medium" mb={2}>
                Tag/Branch <Text as="span" color="red.500">*</Text>
              </Text>
              <Box position="relative">
                <Box
                  as="button"
                  w="100%"
                  p={3}
                  bg={!githubRepo ? "gray.50" : "white"}
                  border="1px solid"
                  borderColor={
                    validationErrors.githubTag ? "red.500" :
                    tagDropdown.isOpen ? "blue.500" : "gray.300"
                  }
                  borderRadius="md"
                  textAlign="left"
                  fontSize="md"
                  _disabled={{ opacity: 0.6, cursor: "not-allowed" }}
                  cursor={!githubRepo ? "not-allowed" : "pointer"}
                  pointerEvents={!githubRepo ? "none" : "auto"}
                  opacity={!githubRepo ? 0.6 : 1}
                  onClick={() => {
                    if (githubRepo) {
                      onTagDropdownToggle(!tagDropdown.isOpen);
                      onTagSearchChange("");
                    }
                  }}
                  _hover={{ borderColor: githubRepo ? (validationErrors.githubTag ? "red.600" : "gray.400") : "gray.300" }}
                  _focus={{
                    borderColor: validationErrors.githubTag ? "red.500" : "blue.500",
                    boxShadow: validationErrors.githubTag 
                      ? "0 0 0 1px var(--chakra-colors-red-500)"
                      : "0 0 0 1px var(--chakra-colors-blue-500)",
                    outline: "none"
                  }}
                >
                  <HStack justify="space-between">
                    <HStack>
                      <Icon as={FiGitBranch} color="gray.500" boxSize={4} />
                      <Text
                        color={githubTag ? "black" : "gray.500"}
                        fontSize="md"
                      >
                        {githubTag || "Select..."}
                      </Text>
                      {isLoadingTags && <Spinner size="sm" />}
                    </HStack>
                    <Icon
                      as={FiChevronDown}
                      transform={tagDropdown.isOpen ? "rotate(180deg)" : "rotate(0deg)"}
                      transition="transform 0.2s"
                    />
                  </HStack>
                </Box>

                {tagDropdown.isOpen && (
                  <Box
                    position="absolute"
                    top="100%"
                    left={0}
                    right={0}
                    mt={1}
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="md"
                    shadow="lg"
                    zIndex={10}
                    maxH="400px"
                    overflow="hidden"
                  >
                    <Box p={3} borderBottom="1px solid" borderColor="gray.200">
                      <HStack>
                        <Icon as={FiSearch} color="gray.400" />
                        <Input
                          placeholder="Search tags..."
                          value={tagDropdown.searchQuery}
                          onChange={(e) => onTagSearchChange(e.target.value)}
                          size="sm"
                          border="none"
                          _focus={{
                            outline: "none",
                            boxShadow: "none"
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </HStack>
                    </Box>
                    <Box maxH="300px" overflowY="auto">
                      {filteredTags.map((tag) => {
                        const isMainBranch = tag === "main" || tag === "master";
                        const isVersion = tag.startsWith("v");
                        
                        return (
                          <Box
                            key={tag}
                            px={4}
                            py={3}
                            cursor="pointer"
                            _hover={{ bg: "gray.50" }}
                            onClick={() => {
                              onTagChange(tag);
                              onTagDropdownToggle(false);
                              onTagSearchChange("");
                            }}
                          >
                            <HStack justify="space-between">
                              <Text fontSize="md">{tag}</Text>
                              <Badge
                                colorScheme={isMainBranch ? "green" : isVersion ? "blue" : "gray"}
                                fontSize="xs"
                              >
                                {isMainBranch ? "Default" : isVersion ? "Release" : "Branch"}
                              </Badge>
                            </HStack>
                          </Box>
                        );
                      })}
                      {filteredTags.length === 0 && (
                        <Box px={4} py={3}>
                          <Text color="gray.500" fontSize="sm">
                            No tags found
                          </Text>
                        </Box>
                      )}
                    </Box>
                  </Box>
                )}
              </Box>
              {validationErrors.githubTag && (
                <Text color="red.500" fontSize="sm" mt={1}>
                  {validationErrors.githubTag}
                </Text>
              )}
            </Box>
          </Grid>
        </VStack>
      </Box>
    </Box>
  );
};